import { useMemo, useState } from 'react';

import {
  createColumnHelper,
  FilterFn,
  filterFns,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import moment from 'moment';
import { useNavigate, useParams } from 'react-router-dom';

import { Permission } from '../../../../../../../../../../../backend/src/auth/permissions/permission';
import { DocChronologySummary } from '../../../../../../../../../../../backend/src/matter/subset/chronology/doc-chronology/dto/get-doc-chronologies-response.dto';
import { useDeleteDocChronology } from '@/api/mutations/useDeleteDocChronology';
import { useGetDocChronologies } from '@/api/queries/useGetDocChronologies';
import { useGetMatterDocChronologies } from '@/api/queries/useGetMatterDocChronologies';
import { useGetSubset } from '@/api/queries/useGetSubset';
import { useUserContext } from '@/Contexts/User';
import formatDate from '@/helpers/formatDate';
import { PermissionGate } from '@/hooks/useHasPermission';
import { Chip } from '@/new/components/chip';
import { ConfirmationModal } from '@/new/components/dialogs/confirmation-dialog';
import { FilterPopoverContent } from '@/new/components/filter-popover-content';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Label } from '@/new/components/shadcn/label';
import { Switch } from '@/new/components/shadcn/switch';
import { ScrollableTable } from '@/new/components/table/scrollable-table';
import { TableNullField } from '@/new/components/table/table-null-field';
import { CHIP_COLOURS } from '@/new/lib/chip-colours';
import { cn } from '@/new/lib/utils';

interface DocChronologyGlobalFilter {
  query: string;
  hideSubsetChronologies: boolean;
}

const fallbackTableData: DocChronologySummary[] = [];
const columnHelper = createColumnHelper<DocChronologySummary>();

function ActionsCell({
  docChronology,
  matterId,
  subsetId,
}: {
  docChronology: DocChronologySummary;
  matterId: string;
  subsetId: string;
}) {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const { user } = useUserContext();

  const { mutate: deleteDocChronology, isLoading: isDeleting } = useDeleteDocChronology();

  const handleDeleteDocChronology = async () => {
    deleteDocChronology({
      matterId,
      subsetId: docChronology.caseId,
      docChronologyId: docChronology.id,
    });
    setDeleteModalOpen(false);
  };

  const isCreator = user?.id === docChronology.createdBy?.id;

  return (
    <>
      <PermissionGate permission={Permission.DocChronologyDelete} orAllowed={isCreator}>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="destructive"
            onClick={(e) => {
              e.stopPropagation();
              setDeleteModalOpen(true);
            }}
            className="-my-1"
          >
            Delete
          </Button>
        </div>
      </PermissionGate>

      <ConfirmationModal
        onOpenChange={setDeleteModalOpen}
        isOpen={deleteModalOpen}
        title="Delete document chronology"
        onConfirm={handleDeleteDocChronology}
        isLoading={isDeleting}
      >
        <p>Are you sure you want to delete this document chronology?</p>
      </ConfirmationModal>
    </>
  );
}

const getColumns = (matterId: string, subsetId: string) => [
  columnHelper.accessor('title', {
    header: () => <span>Title</span>,
    cell: (info) => (
      <div className="flex items-center gap-2">
        {info.row.original.case && (
          <Chip className={cn(CHIP_COLOURS.subset, 'border')}>
            <Icon className="lucide--group" />
            <span className="line-clamp-1 max-w-42 overflow-ellipsis">{info.row.original.case.name}</span>
          </Chip>
        )}
        <span className="line-clamp-1 break-all">{info.getValue()}</span>
      </div>
    ),
  }),
  columnHelper.accessor('documentCount', {
    header: () => <span>Documents</span>,
    cell: (info) => <span>{info.getValue()}</span>,
  }),
  columnHelper.accessor('createdAt', {
    header: () => <span>Date Created</span>,
    cell: (info) => {
      const date = new Date(info.getValue());
      return <div className="truncate">{formatDate(date)}</div>;
    },
  }),
  columnHelper.accessor('createdBy', {
    header: () => <span>Created By</span>,
    cell: (info) => {
      const createdBy = info.getValue();
      return <span>{createdBy?.email || 'Unknown'}</span>;
    },
  }),
  columnHelper.accessor('editedBy', {
    header: () => <span>Last Updated By</span>,
    cell: (info) => {
      const editedBy = info.getValue();
      return editedBy ? <span>{editedBy.email}</span> : <TableNullField />;
    },
  }),
  columnHelper.accessor('editedDate', {
    header: () => <span>Last Updated</span>,
    cell: (info) => {
      const editedDate = info.getValue();
      const lastUpdatedDate = editedDate || info.row.original.createdAt;
      return <span>{moment(new Date(lastUpdatedDate)).fromNow()}</span>;
    },
  }),
  columnHelper.display({
    id: 'actions',
    header: () => <span>Actions</span>,
    cell: (info) => <ActionsCell docChronology={info.row.original} matterId={matterId} subsetId={subsetId} />,
  }),
];

export function DocChronologiesTable() {
  const [globalFilter, setGlobalFilter] = useState<DocChronologyGlobalFilter>({
    query: '',
    hideSubsetChronologies: false,
  });
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) return null;

  const navigate = useNavigate();

  const clearAllFilters = () => {
    setGlobalFilter({ query: '', hideSubsetChronologies: false });
  };

  // Fetch subset doc chronologies
  const { data: subsetDocChronologies, isLoading: isLoadingSubsetDocChronologies } = useGetDocChronologies({
    matterId,
    subsetId,
  });

  // Fetch case data to check if this is the "All Documents" subset
  const { data: subset } = useGetSubset(matterId, subsetId);

  // Conditionally fetch matter doc chronologies only if this is the All Documents subset
  const { data: matterDocChronologies, isLoading: isLoadingMatterDocChronologies } = useGetMatterDocChronologies({
    matterId: subset?.isAllDocuments ? matterId : null,
  });

  // Merge and deduplicate doc chronologies
  const allDocChronologies = useMemo(() => {
    if (!subsetDocChronologies) return fallbackTableData;
    if (!subset?.isAllDocuments || !matterDocChronologies) return subsetDocChronologies;

    // Create a Set of doc chronology IDs from subset doc chronologies to avoid duplicates
    const subsetDocChronologyIds = new Set(subsetDocChronologies.map((c) => c.id));

    // Filter out matter doc chronologies that are already in subset doc chronologies
    const uniqueMatterDocChronologies = matterDocChronologies.filter((c) => !subsetDocChronologyIds.has(c.id));

    // Combine and sort with All Documents doc chronologies first, then by created date within each group
    return [...subsetDocChronologies, ...uniqueMatterDocChronologies].sort((a, b) => {
      const aIsFromCurrentSubset = a.caseId === subsetId;
      const bIsFromCurrentSubset = b.caseId === subsetId;

      // If one is from current subset and the other isn't, prioritize the current subset
      if (aIsFromCurrentSubset && !bIsFromCurrentSubset) return -1;
      if (!aIsFromCurrentSubset && bIsFromCurrentSubset) return 1;

      // Otherwise, sort by created date (most recent first)
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [subsetDocChronologies, matterDocChronologies, subset?.isAllDocuments, subsetId]);

  // Custom global filter function
  const customGlobalFilter: FilterFn<DocChronologySummary> = (
    row,
    columnId,
    filterValue: DocChronologyGlobalFilter,
  ) => {
    const { query, hideSubsetChronologies } = filterValue;

    // Filter out doc chronologies from other subsets if hidden
    if (hideSubsetChronologies && row.original.caseId !== subsetId) {
      return false;
    }

    // Apply text search filter
    if (query) {
      return filterFns.includesString(row, columnId, query, () => null);
    }

    return true;
  };

  const columns = useMemo(() => getColumns(matterId, subsetId), [matterId, subsetId]);

  const table = useReactTable({
    data: allDocChronologies,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: customGlobalFilter,
    initialState: {
      pagination: {
        pageSize: 15,
      },
    },
    state: {
      globalFilter,
    },
  });

  if (!subsetDocChronologies) return null;

  const isAllDocumentsSubset = subset?.isAllDocuments;

  return (
    <ScrollableTable
      table={table}
      plural="doc chronologies"
      globalFilter={globalFilter.query}
      setGlobalFilter={(value) =>
        setGlobalFilter((prev) => ({
          ...prev,
          query: typeof value === 'function' ? value(prev.query) : value,
        }))
      }
      filters={
        isAllDocumentsSubset
          ? {
              popover: (
                <FilterPopoverContent clearAllFilters={clearAllFilters}>
                  <div className="flex items-center gap-2">
                    <Switch
                      id="hide-subset-doc-chronologies"
                      checked={globalFilter.hideSubsetChronologies}
                      onCheckedChange={(checked) =>
                        setGlobalFilter({ ...globalFilter, hideSubsetChronologies: checked })
                      }
                    />
                    <Label htmlFor="hide-subset-doc-chronologies" className="m-0">
                      Hide doc chronologies from other subsets
                    </Label>
                  </div>
                </FilterPopoverContent>
              ),
              clearAll: clearAllFilters,
            }
          : undefined
      }
      rowOnClick={(row) => {
        navigate(
          row.original.case
            ? `/matters/${matterId}/subsets/${row.original.caseId}/chronologies/doc/${row.original.id}`
            : `/matters/${matterId}/subsets/${subsetId}/chronologies/doc/${row.original.id}`,
        );
      }}
      loading={isLoadingSubsetDocChronologies || isLoadingMatterDocChronologies}
    />
  );
}
